import * as React from "react";
import { Component } from "react";
import { Device, GraphDataPoint, GraphLine } from "../dec";
const d3 = require("d3");

interface Props {
  graphData: GraphDataPoint[];
  devices: Device[];
  testInstance: any;
}

interface State {}

class BatteryStatsGraph extends Component<Props, State> {
  private graphLines: GraphLine[];

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    if (this.graphLines) {
      this.drawBatteryStateGraph(this.graphLines);
    }
    this.forceUpdate();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    //Rearrange data by device, into lines
    this.graphLines = [];
    this.props.graphData.forEach(dataPoint => {
      let dataObject = {
        time: dataPoint.time.toJSON(),
        percentage: Number(dataPoint.percentage)
      };

      const line: GraphLine = this.graphLines.find(
        graphLine => graphLine.name === dataPoint.name
      );

      if (line) {
        line.values.push(dataObject);
      } else {
        // Retrieve device model
        const matchingDevice = this.props.devices.find(
          ({ name }) => name === dataPoint.name
        );

        this.graphLines.push({
          name: dataPoint.name,
          isShowing: dataPoint.isShowing,
          model: matchingDevice.model,
          osVersion: matchingDevice.systemState
            ? matchingDevice.systemState.osVersion
            : "OS not recorded",
          values: [dataObject],
          sdkDPH : !isNaN(matchingDevice.sdkDPH) ? +matchingDevice.sdkDPH.toFixed(2) : undefined,
        });
      }
    });

    this.graphLines.forEach(graphLine => {
      let minPercentage = 100;
      let maxPercentage = 0;
      let minTime = new Date().getTime();
      let maxTime = 0;

      let filteredPercentages = [];
      let minTimesForPercentage = {};
      let maxTimesForPercentage = {};

      graphLine.values.forEach(value => {
        minPercentage = Math.min(minPercentage, value.percentage);
        maxPercentage = Math.max(maxPercentage, value.percentage);
        minTime = Math.min(minTime, new Date(value.time).getTime());
        maxTime = Math.max(maxTime, new Date(value.time).getTime());

        if (filteredPercentages.includes(value.percentage)) {
          if (
            new Date(minTimesForPercentage[value.percentage]).getTime() >
            new Date(value.time).getTime()
          ) {
            minTimesForPercentage[value.percentage] = value.time;
          }
          if (
            new Date(maxTimesForPercentage[value.percentage]).getTime() <
            new Date(value.time).getTime()
          ) {
            maxTimesForPercentage[value.percentage] = value.time;
          }
        } else {
          filteredPercentages.push(value.percentage);
          minTimesForPercentage[value.percentage] = value.time;
          maxTimesForPercentage[value.percentage] = value.time;
        }
      });

      //Remove duplicate values at the same percentage, keeping only the first and last for each device
      graphLine.values = graphLine.values.filter(
        value =>
          minTimesForPercentage[value.percentage] === value.time ||
          maxTimesForPercentage[value.percentage] === value.time
      );

      let duration = maxTime - minTime;
      const MILLISECONDS_IN_HOUR = 60 * 60 * 1000;
      let numHours = duration / MILLISECONDS_IN_HOUR;
      graphLine.totalDrain = Math.round(maxPercentage - minPercentage);
      graphLine.stackedDPH = Math.round((graphLine.totalDrain / numHours) * 10) / 10;
    });

    if (this.graphLines) {
      this.drawBatteryStateGraph(this.graphLines);
    }
  }

  drawBatteryStateGraph(data) {
    let self = this;

    d3.select(".battery-stats-graph")
      .selectAll("*")
      .remove();

    let maxPercentage = 0;
    let minPercentage = 100;
    let maxTime = new Date(0);
    let minTime = new Date();

    data.forEach(dataPoint => {
      dataPoint.values.forEach(innerDataPoint => {
        maxPercentage = Math.max(maxPercentage, innerDataPoint.percentage);
        minPercentage = Math.min(minPercentage, innerDataPoint.percentage);

        maxTime = new Date(
          Math.max(maxTime.getTime(), new Date(innerDataPoint.time).getTime())
        );
        minTime = new Date(
          Math.min(minTime.getTime(), new Date(innerDataPoint.time).getTime())
        );
      });
    });
    data.sort((a, b) => (a.name > b.name ? 1 : -1));

    if (!data.length) {
      maxPercentage = 100;
      minPercentage = 0;
      maxTime = new Date(0);
      minTime = new Date(0);
    }

    const width = 1200;
    const height = 500;
    const margin = 50;
    const marginRight = 180;
    const duration = 250;

    const lineOpacity = "0.45";
    const lineOpacityHover = "0.85";
    const otherLinesOpacityHover = "0.1";
    const legendRectOpacity = "0.8";
    const legendRectOpacityHover = "1";
    const legendOtherRectOpacityHover = "0.1";
    const lineStroke = "3px";
    const lineStrokeHover = "5px";

    const hiddenOpacity = "0";

    const circleOpacity = "0.85";
    const circleOpacityOnLineHover = "0.25";
    const circleRadius = 3;
    const circleRadiusHover = 6;

    const xScaleBuffer = 0.02; // 2% of time span (relative)
    const yScaleBuffer = 5; // 5% battery (absolute)

    let parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%S.%L%Z");
    data.forEach(function(d) {
      d.values.forEach(function(d) {
        d.time = parseDate(d.time);
        d.percentage = +d.percentage;
      });
    });

    let timeSpan = maxTime.getTime() - minTime.getTime();

    let xScale = d3
      .scaleTime()
      .domain([
        new Date(minTime.getTime() - timeSpan * xScaleBuffer),
        new Date(maxTime.getTime() + timeSpan * xScaleBuffer)
      ])
      .range([0, width - marginRight]);

    let yScale = d3
      .scaleLinear()
      .domain([
        Math.max(0, minPercentage - yScaleBuffer),
        Math.min(100, maxPercentage + yScaleBuffer)
      ])
      .range([height - margin, 0]);

    let color = d3.scaleOrdinal(d3.schemeCategory10);

    /* Add SVG */

    let svg = d3
      .select(".battery-stats-graph")
      .append("svg")
      .attr("width", width + margin + "px")
      .attr("height", height + margin + "px")
      .append("g")
      .attr("transform", `translate(${margin}, ${margin})`);

    /* Add lines */

    let line = d3
      .line()
      .x(d => xScale(d.time))
      .y(d => yScale(d.percentage));

    let lines = svg.append("g").attr("class", "lines");

    lines
      .selectAll(".line-group")
      .data(data)
      .enter()
      .append("g")
      .attr("class", "line-group")
      .on("mouseover", handleShowTooltip)
      .on("mouseout", handleHideTooltip)
      .append("path")
      .attr("class", "line")
      .attr("id", (d, i) => "line-" + i)
      .attr("d", d => line(d.values))
      .style("stroke", (d, i) => color(i))
      .style("opacity", d => (d.isShowing ? lineOpacity : hiddenOpacity))
      .style("stroke-width", lineStroke)
      .attr("visibility", d => (d.isShowing ? "visible" : "hidden"))
      .on("mouseover", (d, i) => handleMouseOverLine(d, i))
      .on("mouseout", handleMouseOutLine);

    /* Add circles */

    lines
      .selectAll("circle-group")
      .data(data)
      .enter()
      .append("g")
      .style("fill", (d, i) => color(i))
      .style("opacity", d => (d.isShowing ? "1" : hiddenOpacity))
      .selectAll("circle")
      .data(d => d.values)
      .enter()
      .append("g")
      .attr("class", "circle")
      .on("mouseover", function(d) {
        let offsetX = 20;
        let offsetY = 10;

        d3.select(this)
          .style("cursor", "pointer")
          .append("rect")
          .attr("class", "text-rect")
          .attr("x", d => xScale(d.time) + offsetX)
          .attr("y", d => yScale(d.percentage) - 40 + offsetY)
          .attr("width", 140)
          .attr("height", 35)
          .attr("opacity", 0.7)
          .style("fill", "#fff")
          .style("stroke-width", "2px");

        d3.select(this)
          .append("text")
          .attr("class", "text")
          .text(`${Math.round(d.percentage)}% at ${self.formatDate(d.time)}`)
          .attr("x", d => xScale(d.time) + 5 + offsetX)
          .attr("y", d => yScale(d.percentage) - 15 + offsetY);
      })
      .on("mouseout", function(d) {
        d3.select(this)
          .style("cursor", "none")
          .transition()
          .duration(duration)
          .selectAll(".text")
          .remove();
        d3.select(this)
          .selectAll(".text-rect")
          .remove();
      })
      .append("circle")
      .attr("cx", d => xScale(d.time))
      .attr("cy", d => yScale(d.percentage))
      .attr("r", circleRadius)
      .style("opacity", circleOpacity)
      .on("mouseover", function(d) {
        d3.select(this)
          .transition()
          .duration(duration)
          .attr("r", circleRadiusHover);
      })
      .on("mouseout", function(d) {
        d3.select(this)
          .transition()
          .duration(duration)
          .attr("r", circleRadius);
      });

    /* Add legend */

    let legendRectSize = 18;
    let legendSpacing = 4;

    let legend = svg
      .selectAll(".legend")
      .data(data)
      .enter()
      .append("g")
      .attr("class", "legend")
      .attr("transform", (d, i) => {
        let height = legendRectSize + legendSpacing;
        let horz = width - 150;
        let vert = i * height;
        return "translate(" + horz + "," + vert + ")";
      });

    legend
      .append("rect")
      .attr("class", "legend-rect")
      .attr("id", (d, i) => "legend-rect-" + i)
      .attr("width", 18)
      .attr("height", 15)
      .style("fill", (d, i) => color(i))
      .style("stroke", (d, i) => color(i))
      .style("opacity", d => (d.isShowing ? "1" : hiddenOpacity))
      .on("mouseover", (d, i) => {
        handleMouseOverLine(d, i);
        handleShowTooltip(d, i, false);
      })
      .on("mouseout", (d, i) => {
        handleMouseOutLine(d, i);
        handleHideTooltip(d, i);
      });

    legend
      .append("text")
      .attr("x", 25)
      .attr("y", 10)
      .attr("class", "legend-text")
      .attr("id", (d, i) => "legend-text-" + i)
      .style("fill", (d, i) => color(i))
      .style("opacity", d => (d.isShowing ? "1" : hiddenOpacity))
      .text(d => `${d.name} (${d.stackedDPH}${!isNaN(d.sdkDPH) ? (", " + d.sdkDPH.toFixed(1)) : ""})`)
      .on("mouseover", (d, i) => {
        handleMouseOverLine(d, i);
        handleShowTooltip(d, i, false);
      })
      .on("mouseout", (d, i) => {
        handleMouseOutLine(d, i);
        handleHideTooltip(d, i);
      });

    /* Add axes */

    let xAxis = d3.axisBottom(xScale).ticks(10);
    let yAxis = d3.axisLeft(yScale).ticks(10);

    svg
      .append("g")
      .attr("class", "x axis")
      .attr("transform", `translate(0, ${height - margin})`)
      .call(xAxis)
      .append("text")
      .attr("x", (width - marginRight) / 2)
      .attr("y", 50)
      .attr("fill", "#000")
      .text("Time");

    svg
      .append("g")
      .attr("class", "y axis")
      .call(yAxis)
      .append("text")
      .attr("y", -40)
      .attr("x", -height / 2 + 30)
      .attr("transform", "rotate(-90)")
      .attr("fill", "#000")
      .text("Battery %");

    /* Edge case : empty dataset */

    if (!data.length || data.every(({ isShowing }) => !isShowing)) {
      svg
        .append("text")
        .attr("x", (width - marginRight) / 2)
        .attr("y", (height - margin) / 2)
        .attr("text-anchor", "middle")
        .text("No data");
    }

    /* Helper functions */

    function handleMouseOverLine(d, i) {
      if (d.isShowing) {
        d3.selectAll(".line").style("opacity", d =>
          d.isShowing ? otherLinesOpacityHover : hiddenOpacity
        );
        d3.selectAll(".circle").style("opacity", circleOpacityOnLineHover);
        d3.selectAll(".legend-rect").style("opacity", d =>
          d.isShowing ? legendOtherRectOpacityHover : hiddenOpacity
        );
        d3.select("#legend-rect-" + i).style("opacity", legendRectOpacityHover);
        d3.selectAll(".legend-text").style("opacity", d =>
          d.isShowing ? legendOtherRectOpacityHover : hiddenOpacity
        );
        d3.select("#legend-text-" + i).style("opacity", legendRectOpacityHover);
        d3.select("#line-" + i)
          .style("opacity", lineOpacityHover)
          .style("stroke-width", lineStrokeHover)
          .style("cursor", "pointer");
      }
    }

    function handleMouseOutLine(d, i) {
      d3.selectAll(".line").style("opacity", d =>
        d.isShowing ? lineOpacity : hiddenOpacity
      );
      d3.selectAll(".circle").style("opacity", circleOpacity);
      d3.selectAll(".legend-rect").style("opacity", d =>
        d.isShowing ? legendRectOpacity : hiddenOpacity
      );
      d3.selectAll(".legend-text").style("opacity", d =>
        d.isShowing ? legendRectOpacity : hiddenOpacity
      );
      d3.select("#line-" + i)
        .style("stroke-width", lineStroke)
        .style("cursor", "none");
    }

    function handleShowTooltip(d, i, followMouse = true) {
      if (d.isShowing) {
        let mouseX, mouseY;
        if (followMouse) {
          mouseX = d3.mouse(this)[0];
          mouseY = d3.mouse(this)[1];
        } else {
          mouseX = width - 340;
          mouseY = 30 + (legendRectSize + legendSpacing) * i;
        }
        let offsetX = 20;
        let offsetY = -60;
        svg
          .append("rect")
          .attr("class", "title-text-rect")
          .attr("x", mouseX + offsetX)
          .attr("y", mouseY + offsetY)
          .attr("width", 140)
          .attr("height", 130)
          .attr("opacity", 0.7)
          .style("fill", "#fff")
          .style("stroke", "#000")
          .style("stroke-width", "2px");
        svg
          .append("text")
          .attr("class", "title-text")
          .style("fill", "#000")
          .text(`${d.name}`)
          .attr("text-anchor", "start")
          .attr("x", mouseX + offsetX + 5)
          .attr("y", mouseY + offsetY + 20);
        svg
          .append("text")
          .attr("class", "title-text")
          .style("fill", "#000")
          .text(`osVersion: ${d.osVersion}`)
          .attr("text-anchor", "start")
          .attr("x", mouseX + offsetX + 5)
          .attr("y", mouseY + offsetY + 40);
        svg
          .append("text")
          .attr("class", "title-text")
          .style("fill", "#000")
          .text(`${d.model}`)
          .attr("text-anchor", "start")
          .attr("x", mouseX + offsetX + 5)
          .attr("y", mouseY + offsetY + 60);
        svg
          .append("text")
          .attr("class", "title-text")
          .style("fill", "#000")
          .text(`Drain: ${d.totalDrain}%`)
          .attr("text-anchor", "start")
          .attr("x", mouseX + offsetX + 5)
          .attr("y", mouseY + offsetY + 80);
        svg
          .append("text")
          .attr("class", "title-text")
          .style("fill", "#000")
          .text(`Stacked DPH: ${d.stackedDPH}%`)
          .attr("text-anchor", "start")
          .attr("x", mouseX + offsetX + 5)
          .attr("y", mouseY + offsetY + 100);
        svg
          .append("text")
          .attr("class", "title-text")
          .style("fill", "#000")
          .text(`${!isNaN(d.sdkDPH) ? ("SDK DPH: " + d.sdkDPH.toFixed(1) + "%") : ""}`)
          .attr("text-anchor", "start")
          .attr("x", mouseX + offsetX + 5)
          .attr("y", mouseY + offsetY + 120);
      }
    }

    function handleHideTooltip(d, i) {
      svg.selectAll(".title-text").remove();
      svg.selectAll(".title-text-rect").remove();
    }
  }

  formatDate(date) {
    return new Date(date).toLocaleTimeString("en-us");
  }

  render() {
    return (
      <svg className="battery-stats-graph">
        <div className="legend" />
      </svg>
    );
  }
}

export default BatteryStatsGraph;
